/* eslint-disable react/no-unescaped-entities */
/**
 * HomeBooking — Page d'accueil Confilex (plateforme de mise en relation IA).
 *
 * Règle UX v3.45 : minimalisme
 *   - 3 sections seulement (Hero, Parcours 3 étapes, CTA spécialiste)
 *   - Pas de liste d'annuaire sur la home (le réseau de spécialistes reste
 *     accessible via /reseau-specialistes ou via l'orientation guidée)
 *   - Aucun bloc redondant : chaque promesse apparaît une seule fois
 */
import { Link } from "react-router-dom";
import BookingNavbar from "@/components/booking/BookingNavbar";
import SmartSearchHero from "@/components/booking/SmartSearchHero";
import Footer from "@/components/Footer";
import Seo, { ldOrganization, ldWebsite } from "@/components/Seo";
import { ArrowRight, Crown } from "lucide-react";

export default function HomeBooking() {
  return (
    <div className="cb-shell" data-testid="home-booking">
      <Seo
        title="Confilex — Trouvez le spécialiste adapté à votre situation"
        description="Plateforme premium de mise en relation avec les professions réglementées : avocats, notaires, experts-comptables. Préparation IA de votre dossier avant le spécialiste."
        canonical={typeof window !== "undefined" ? window.location.origin + "/" : undefined}
        jsonLd={[ldOrganization(), ldWebsite()]}
      />

      <BookingNavbar />

      {/* 1. HERO — la seule porte d'entrée */}
      <SmartSearchHero />

      {/* 2. PARCOURS EN 3 ÉTAPES — respiration maximale, aucun autre bloc */}
      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-24 lg:py-32 text-center">
        <span className="text-[10px] uppercase tracking-[0.28em] cb-gold font-semibold">
          Le parcours CONFILEX
        </span>
        <h2 className="cb-serif text-3xl lg:text-5xl font-semibold cb-text-ivory mt-4 leading-[1.15]">
          Trois étapes. Nous cherchons pour vous.
        </h2>
        <p className="text-base text-[#BFC8C2] mt-6 max-w-xl mx-auto leading-relaxed">
          Vous décrivez votre situation. L'IA structure votre dossier. Nous sélectionnons
          dans notre réseau de spécialistes les 3 profils les plus pertinents au moment
          où votre dossier est prêt.
        </p>

        <div className="grid sm:grid-cols-3 gap-6 lg:gap-8 mt-16 text-left" data-testid="home-steps">
          {[
            { n: "01", t: "Décrivez votre situation", d: "Quelques mots suffisent. Sans jargon." },
            { n: "02", t: "L'IA structure le dossier", d: "Chronologie, pièces, questions, urgence." },
            { n: "03", t: "3 spécialistes sélectionnés", d: "Uniquement lorsque votre dossier est prêt." },
          ].map((s) => (
            <div key={s.n} className="cb-card p-7" data-testid={`home-step-${s.n}`}>
              <div className="text-[11px] uppercase tracking-[0.24em] cb-gold font-bold mb-4">
                Étape {s.n}
              </div>
              <p className="cb-serif text-lg cb-text-ivory font-semibold leading-snug">
                {s.t}
              </p>
              <p className="text-sm text-[#BFC8C2] mt-3 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>

        <Link
          to="/dossier"
          className="cb-btn-gold inline-flex items-center justify-center gap-2 px-8 py-4 text-sm mt-16"
          data-testid="home-start-dossier"
        >
          Commencer mon dossier <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      {/* 3. CTA EXPERT — minimaliste, une seule ligne visuelle */}
      <section className="border-t border-[#063D25]">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-20 lg:py-24 text-center">
          <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] cb-gold font-semibold">
            <Crown className="w-3.5 h-3.5" /> Espace spécialiste
          </span>
          <h2 className="cb-serif text-2xl lg:text-3xl font-semibold cb-text-ivory mt-4 leading-tight max-w-xl mx-auto">
            Recevez des dossiers structurés, qualifiés, prêts à exploiter.
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/expert/apply" className="cb-btn-gold inline-flex items-center justify-center gap-2"
                  data-testid="home-become-expert-cta">
              Devenir spécialiste référencé <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/pricing" className="cb-btn-ghost inline-flex items-center justify-center text-sm">
              Voir les tarifs
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
